import { X, Check } from "lucide-react";

export default function BeforeAfter() {
  return (
    <section
      id="before-after"
      data-testid="before-after-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <span className="text-xs tracking-[0.22em] uppercase text-blue-400 font-medium">
            Before / After
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-semibold mt-4 leading-[1.1]">
            Sometimes you don&apos;t need a new identity —{" "}
            <span className="italic">just a cleaner one</span>.
          </h2>
          <p className="mt-5 text-white/55 max-w-2xl text-sm md:text-base">
            Sometimes a business doesn&apos;t need a completely new identity —
            it just needs a cleaner, faster, more professional version of what
            already works.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6">
          {/* Before */}
          <div
            data-testid="before-card"
            className="relative rounded-2xl border border-white/10 bg-[#0e0e0e] p-2 overflow-hidden"
          >
            <div className="absolute top-5 left-5 z-10 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-[10px] tracking-[0.22em] uppercase text-red-300 font-medium">
              Before
            </div>
            <div className="rounded-xl bg-[#f4f1ec] p-7 md:p-9 min-h-[360px]">
              {/* mock outdated site */}
              <div className="font-serif text-2xl text-[#0a0a0a] tracking-tight">
                Joe&apos;s Roofing Services LLC
              </div>
              <div className="mt-1 text-xs text-[#7a6a4a] underline">
                www.joesroofingllc.net
              </div>
              <div className="mt-5 h-2 w-3/4 bg-[#cdbf99] rounded-sm" />
              <div className="mt-2 h-2 w-2/3 bg-[#cdbf99] rounded-sm" />
              <div className="mt-2 h-2 w-1/2 bg-[#cdbf99] rounded-sm" />
              <div className="mt-7 inline-block bg-[#b35a1f] text-white text-xs px-4 py-2 rounded-none border border-[#8a3f10]">
                CLICK HERE FOR FREE ESTIMATE!!!
              </div>
              <div className="mt-6 text-[11px] text-[#5b5040] italic">
                ⚠ Best viewed in Internet Explorer
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <div className="aspect-video bg-[#d8caa6]" />
                <div className="aspect-video bg-[#d8caa6]" />
                <div className="aspect-video bg-[#d8caa6]" />
              </div>
            </div>
            <ul className="px-3 py-5 space-y-2 text-sm text-white/70">
              {[
                "Outdated layout",
                "Weak, generic CTAs",
                "Hard to navigate on mobile",
                "Looks untrustworthy to new visitors",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                    <X size={11} className="text-red-300" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>

          {/* After */}
          <div
            data-testid="after-card"
            className="relative rounded-2xl border border-blue-500/30 bg-[#0e0e0e] p-2 overflow-hidden shadow-[0_30px_80px_-40px_rgba(59,130,246,0.5)]"
          >
            <div className="absolute top-5 left-5 z-10 px-2.5 py-1 rounded-full bg-blue-500/10 border border-blue-500/40 text-[10px] tracking-[0.22em] uppercase text-blue-300 font-medium">
              After
            </div>
            <div className="rounded-xl bg-[#0A0A0A] border border-white/10 p-7 md:p-9 min-h-[360px]">
              <div className="flex items-center justify-between">
                <div className="font-display text-2xl text-white tracking-tight">
                  Summit Roofing
                </div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/40">
                  Licensed · Insured
                </div>
              </div>
              <div className="mt-7 font-display text-3xl text-white leading-tight">
                Free roof estimates in <span className="italic">24 hours</span>.
              </div>
              <div className="mt-3 text-sm text-white/60 max-w-md">
                Licensed, insured, and built around clear, honest pricing — no pressure.
              </div>
              <div className="mt-6 flex items-center gap-3">
                <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500 text-white text-xs font-medium">
                  Get free estimate
                </div>
                <div className="inline-flex items-center px-4 py-2 rounded-full border border-white/15 text-white text-xs">
                  Call (305) 555-0142
                </div>
              </div>
              <div className="mt-6 grid grid-cols-3 gap-2">
                <div className="aspect-video rounded-md bg-white/[0.04] border border-white/10" />
                <div className="aspect-video rounded-md bg-white/[0.04] border border-white/10" />
                <div className="aspect-video rounded-md bg-white/[0.04] border border-white/10" />
              </div>
            </div>
            <ul className="px-3 py-5 space-y-2 text-sm text-white/80">
              {[
                "Clean, modern design",
                "Strong trust signals",
                "Easier contact + clear CTAs",
                "Mobile-first, fast loading",
              ].map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-blue-500/10 border border-blue-500/40 flex items-center justify-center">
                    <Check size={11} className="text-blue-300" />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
